<template>
  <el-dialog
    :visible="true"
    :fullscreen="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div slot="title">
      {{ title }}
      <el-tag
        v-if="representativeOrder.status && (type === 'update' || type === 'view')"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <el-alert
      v-if="representativeOrder.status === 'done' && type !== 'view'"
      title="Ez a raktárközi megrendelés már le lett zárva, ezért nem módosítható."
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 30px; margin-top: -20px;"
    />
    <div v-if="type === 'view' && representativeOrder.receipt_number">
      <el-link @click="openReceipt" type="primary" style="margin-bottom: 30px; margin-top: -20px;">
        <i class="el-icon-printer el-icon--right"></i>
        {{ representativeOrder.receipt_number }}
      </el-link>
    </div>
    <div v-loading="loading">
      <el-form
        ref="orderForm"
        label-position="top"
        :model="representativeOrder"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="supplier"
              label="Szállító"
            >
              <el-select
                v-model="representativeOrder.supplier"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="representativeOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="source_storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Szállitó raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="`Ebből a raktárból küldjük vissza az árut. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                  placement="right"
                >
                  <i class="el-icon-info"/>
                </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="representativeOrder.source_storage"
                style="width: 100%;"
                value-key="id"
                :disabled="representativeOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="customer"
              label="Átvevő"
            >
              <el-select
                v-model="representativeOrder.customer"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="representativeOrder.status !== 'recorded' || type === 'view'"
                @change="onCustomerChange"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'customer'"
                  :value="customer"
                  :label="`${customer.last_name} ${customer.first_name}`"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="target_storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Átvevő raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                <el-tooltip
                  class="item"
                  effect="dark"
                  content="Ebből a raktárból küldjük vissza az árut."
                  placement="right"
                >
                  <i class="el-icon-info"/>
                </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="representativeOrder.target_storage"
                style="width: 100%;"
                value-key="id"
                :disabled="representativeOrder.status !== 'recorded' || !representativeOrder.customer || type === 'view'"
              >
                <el-option
                  v-for="storage in representativeStorages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="reference_number"
              label="Hivatkozási szám"
            >
              <el-input
                v-model="representativeOrder.reference_number"
                :disabled="representativeOrder.status !== 'recorded' || type === 'view'"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="representativeOrder.currency"
                :disabled="representativeOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="completed_at"
              label="Teljesítés dátuma"
            >
              <el-date-picker
                v-model="representativeOrder.completed_at"
                type="date"
                :disabled="representativeOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          :default-items="representativeOrder.order_items"
          :enabled-elements="enabledElements"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'supplierSelector'] : ['product', 'productPackType', 'expirationDateSelector', 'count', 'unitPrice', 'netPrice', 'vat', 'actions', 'supplierSelector']"
          compare-type="completion"
          :storage-id="representativeOrder.source_storage && representativeOrder.source_storage.id"
          :compare-items="representativeOrder.inward_handling && representativeOrder.inward_handling.inward_handling_items"
          @priceChange="onPriceChange"
        />
      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ representativeOrder.net_price | currency }}</strong></div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ representativeOrder.gross_price | currency }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="representativeOrder.status !== 'recorded' && representativeOrder.receipt_file"
        @click="openReceipt"
      >
        Bizonylat nyomtatása
      </el-button>
      <el-button
        :disabled="representativeOrder.status !== 'recorded'"
        :loading="saveButtonLoading"
        type="primary"
        @click="store"
      >
        Mentés
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import ProductItems from '../../utils/ProductItems/index';

export default {
  components: {
    ProductItems,
  },
  props: ['type', 'representativeOrderId'],
  data() {
    return {
      loading: false,
      receiptPrefix: '',
      buttonLoading: false,
      saveButtonLoading: false,
      representativeStorages: [],
      representativeOrder: {
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        source_storage: {},
        target_storage: {},
        customer: null,
        supplier: {},
        completed_at: null,
      },
      rules: {
        customer: [
          {
            required: true,
            message: 'Vevő kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        supplier: [
          {
            required: true,
            message: 'Beszállító kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        source_storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        target_storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        completed_at: [
          {
            required: true,
            message: 'Teljesítés megadása kötelező',
            trigger: 'change'
          }
        ],
        currency: [
          {
            required: true,
            message: 'Deviza megadása kötelező',
            trigger: 'change'
          }
        ],
      },
    };
  },
  computed: {
    title() {
      if (this.type === 'add') {
        return 'Új raktárközi megrendelés';
      } else if (this.type === 'update') {
        return 'Raktárközi megrendelés módosítása';
      } else if (this.type === 'view') {
        return 'Raktárközi megrendelés megtekintése';
      } else {
        return '';
      }
    },
    buttonText() {
      if (this.type === 'add') {
        return 'Hozzáadása';
      } else if (this.type === 'update') {
        return 'Módosítás';
      } else {
        return '';
      }
    },
    enabledElements() {
      if(this.type === 'view') {
        return [];
      }
      switch (this.representativeOrder.status) {
        case 'recorded':
          return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice', 'expirationDateSelector', 'supplierSelector'];
        case 'sent':
          return [];
        case 'done':
          return [];
        default:
          return [];
      }
    },
    suppliers() {
      return this.$store.getters['ownCompany/ownCompanies'];
    },
    customers() {
      return this.$store.getters['user/users'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
    currencies() {
      return this.$store.getters['currency/currencies'];
    },
  },
  async mounted() {
    this.loading = true;
    const requiredFetches = [];
    if (!this.customers.length) {
      requiredFetches.push(this.$store.dispatch('user/fetchAll', ''));
    }
    if (!this.storages.length) {
      requiredFetches.push(this.$store.dispatch('storage/fetchAll', ''));
    }
    if (!this.currencies.length) {
      requiredFetches.push(this.$store.dispatch('currency/fetchAll', ''));
    }
    if (!this.suppliers.length) {
      requiredFetches.push(this.$store.dispatch('ownCompany/fetchAll', ''));
    }
    if (!this.$store.getters['vat/vats'].length) {
      requiredFetches.push(this.$store.dispatch('vat/fetchAll', ''));
    }

    await Promise.all(requiredFetches);

    this.receiptPrefix = (await axios.get('api/receipt-prefixes/representativeOrder')).full_prefix;
    if (this.representativeOrderId) {
      this.representativeOrder = await this.$store.dispatch('representativeOrder/fetch', this.representativeOrderId);
      this.representativeStorages.push(this.representativeOrder.target_storage);
      if(this.type === 'add') {
        this.representativeOrder.status = 'recorded';
        this.representativeOrder.receipt_number = null;
        this.representativeOrder.order_items = this.representativeOrder.order_items.map(item => {
          delete item.id;
          return item;
        });
      }
    } else {
      this.representativeOrder.supplier = this.suppliers[0];
      this.representativeOrder.currency = this.currencies[0];
      this.representativeOrder.source_storage = this.storages[0];
      this.representativeOrder.completed_at = moment()
        .format('Y-MM-DD');
    }
    this.loading = false;
  },
  methods: {
    async onCustomerChange() {
      this.representativeStorages = await axios.get('api/storages/user/' + this.representativeOrder.customer.id);
      if (this.representativeStorages.length) {
        this.representativeOrder.target_storage = this.representativeStorages[0];
      } else {
        this.representativeOrder.target_storage = {};
      }
    },
    onPriceChange({ netPrice, grossPrice }) {
      this.representativeOrder.net_price = netPrice;
      this.representativeOrder.gross_price = grossPrice;
    },
    handleClose() {
      if (this.type === 'view') {
        return this.$router.replace('/sales/representative-orders');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/sales/representative-orders');
        })
        .catch(() => {
        });
    },
    async store() {
      this.$refs.orderForm.validate(async valid => {
        if (valid) {
          this.representativeOrder.order_items = this.$refs['productItems'].getItems();
          if (!this.representativeOrder.order_items.length) {
            return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
          } else if (!this.$refs['productItems'].validateProducts()) {
            return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
          }
          this.saveButtonLoading = true;
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('representativeOrder/create', this.representativeOrder);
            } else if (this.type === 'update') {
              await this.$store.dispatch('representativeOrder/update', this.representativeOrder);
            }
            this.$store.dispatch('representativeOrder/fetchAll');
            this.saveButtonLoading = false;
            this.$router.replace('/sales/representative-orders');
          } catch (e) {
          }
          this.saveButtonLoading = false;
        }
      });
    },
    openReceipt() {
      if (this.representativeOrder.receipt_file && this.representativeOrder.receipt_file.path) {
        window.open(this.representativeOrder.receipt_file.path);
      }
    },
    getStatus() {
      switch (this.representativeOrder.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'sent':
          return {
            label: 'Termékek kiadva',
            tag: 'primary',
          };
        case 'waiting-for-inward':
          return {
            label: 'Bevételezésre vár',
            tag: 'primary',
          };
        case 'done':
          return {
            label: 'Elküldve',
            tag: 'success',
          };
        case 'resigned':
          return {
            label: 'Lemondva',
            tag: 'danger',
          };
      }
    },
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;
    > div {
      display: inline-block;
    }
  }
</style>
